import { normalizeVolumeEncoding } from './chartVizTypes';
import { formatReportingDisplayValue } from './reportingChartUtils';

export const STAT_DISPLAY_AUTO = 'auto';
export const STAT_DISPLAY_TOTAL = 'total';
export const STAT_DISPLAY_BREAKDOWN = 'breakdown';

export const STAT_MAX_BREAKDOWN = 12;

/** Parse API numeric values (bigint may arrive as string). */
export function parseNumericCount(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const n = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(n) ? n : 0;
}


/** Resolve category and value column keys from dataset + encoding. */
export function resolveStatEncoding(dataset, encoding = {}) {
    const { columns = [], rows = [] } = dataset ?? {};
    const { category, value } = encoding;

    const valCols = value
        ? normalizeVolumeEncoding(value)
        : columns.filter(c => c.type === 'number').map(c => c.key);
    const valKey = valCols[0];
    if (!valKey) return { valKey: null, catCol: null, valCols };

    const catCol = category
        || columns.find(c => c.key === 'category')?.key
        || columns.find(c => c.key === 'period')?.key
        || columns.find(c => c.type === 'category' || c.type === 'text' || c.type === 'datetime')?.key
        || (rows.length === 1 && columns.length === 2
            ? columns.find(c => c.key !== valKey && c.type !== 'number')?.key
            : null);

    return { valKey, catCol, valCols };
}

/**
 * @param {object} dataset
 * @param {object} [vizSpec]
 * @returns {{ display: string, items: Array<{ label: string, value: number }>, total: number, valueLabel: string }|null}
 */
export function buildStatViewModel(dataset, vizSpec = {}) {
    if (!dataset?.rows?.length) return null;

    const { encoding = {}, statDisplay = STAT_DISPLAY_AUTO } = vizSpec;
    const { valKey, catCol } = resolveStatEncoding(dataset, encoding);
    if (!valKey) return null;

    const valueLabel = dataset.columns?.find(c => c.key === valKey)?.label || valKey;
    const catColumn = dataset.columns?.find(c => c.key === catCol);

    const items = dataset.rows
        .map(row => ({
            label: catCol
                ? String(formatReportingDisplayValue(row[catCol], catColumn?.type))
                : valueLabel,
            value: parseNumericCount(row[valKey]),
        }))
        .sort((a, b) => b.value - a.value);

    const total = items.reduce((sum, item) => sum + item.value, 0);

    let display = statDisplay;
    if (display === STAT_DISPLAY_AUTO) {
        if (items.length <= 1) display = STAT_DISPLAY_TOTAL;
        else if (items.length <= STAT_MAX_BREAKDOWN) display = STAT_DISPLAY_BREAKDOWN;
        else display = STAT_DISPLAY_TOTAL;
    }

    return { display, items, total, valueLabel };
}

export function isStatVizType(vizSpec) {
    return vizSpec?.type === 'stat';
}
